// Heuristic (not ML) auto-detection of left/right foot placement on a scan. Estimates the
// background color from the border pixels, finds connected blobs of "not background" pixels,
// and keeps the two largest ones (by pixel count) after dropping tiny dust/noise specks. Not
// perfect by design — the caller always leaves the resulting region editable (drag/resize
// already works on region annotations).
const ANALYSIS_WIDTH = 400;
const FOREGROUND_THRESHOLD = 42; // color distance from background before a pixel counts as "foot"
const MIN_FOREGROUND_RATIO = 0.01; // ignore a blob with too few pixels to be a real foot (dust/noise)
const PADDING_RATIO = 0.03; // pad the detected box outward a bit so edges aren't clipped

function colorDistance(r1, g1, b1, r2, g2, b2) {
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

function estimateBackground(data, width, height) {
  let r = 0, g = 0, b = 0, n = 0;
  const sampleRow = (y) => {
    for (let x = 0; x < width; x += 4) {
      const i = (y * width + x) * 4;
      r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
    }
  };
  const sampleCol = (x) => {
    for (let y = 0; y < height; y += 4) {
      const i = (y * width + x) * 4;
      r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
    }
  };
  sampleRow(0); sampleRow(height - 1);
  sampleCol(0); sampleCol(width - 1);
  return n ? { r: r / n, g: g / n, b: b / n } : { r: 0, g: 0, b: 0 };
}

function buildForegroundMask(data, width, height, bg) {
  const mask = new Uint8Array(width * height);
  for (let i = 0, p = 0; p < mask.length; i += 4, p++) {
    if (colorDistance(data[i], data[i + 1], data[i + 2], bg.r, bg.g, bg.b) > FOREGROUND_THRESHOLD) mask[p] = 1;
  }
  return mask;
}

// Iterative (stack-based, not recursive) 4-connected flood fill — groups foreground pixels into
// blobs so the tape measure and dust specks can be told apart from an actual foot by shape/size,
// instead of just taking the bounding box of every non-background pixel (which used to swallow
// the tape measure and the gap around it into one huge box).
function findConnectedComponents(mask, width, height) {
  const visited = new Uint8Array(width * height);
  const components = [];
  for (let start = 0; start < mask.length; start++) {
    if (!mask[start] || visited[start]) continue;
    visited[start] = 1;
    const stack = [start];
    let minX = width, maxX = -1, minY = height, maxY = -1, count = 0;
    while (stack.length) {
      const idx = stack.pop();
      const x = idx % width, y = (idx / width) | 0;
      count++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (x > 0) { const n = idx - 1; if (mask[n] && !visited[n]) { visited[n] = 1; stack.push(n); } }
      if (x < width - 1) { const n = idx + 1; if (mask[n] && !visited[n]) { visited[n] = 1; stack.push(n); } }
      if (y > 0) { const n = idx - width; if (mask[n] && !visited[n]) { visited[n] = 1; stack.push(n); } }
      if (y < height - 1) { const n = idx + width; if (mask[n] && !visited[n]) { visited[n] = 1; stack.push(n); } }
    }
    components.push({ minX, maxX, minY, maxY, count });
  }
  return components;
}

// A blob counts as "foot-shaped" if it's large enough to not be dust, solid enough to not be
// the tape measure's sparse outline, and compact enough to not be the tape measure's long
// straight body (a foot's bounding box is much closer to square than a measuring tape's).
function looksLikeFoot(c, totalPixels) {
  // Only filter out dust/noise by size. Fill-ratio and aspect-ratio checks were tried here to
  // also reject the measuring tape, but real (non-mock) photos have feet at a slight angle to
  // the frame — a tilted foot's axis-aligned bbox is much bigger than its silhouette, which
  // tanked its fill ratio below the old 0.35 threshold and got a real foot rejected, leaving
  // only the other (straighter) foot detected. Connected-components already keeps the tape as
  // its own separate, much smaller blob in the vast majority of cases, so plain size is enough.
  return c.count >= totalPixels * MIN_FOREGROUND_RATIO;
}

// Returns { left: {x1,y1,x2,y2} | null, right: {...} | null } in the ORIGINAL image's pixel
// coordinates — ready to drop straight into a `region` annotation.
export async function detectFootRegions(base64) {
  const src = base64.startsWith("data:") ? base64 : `data:image/jpeg;base64,${base64}`;
  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

  const scale = ANALYSIS_WIDTH / img.naturalWidth;
  const width = ANALYSIS_WIDTH;
  const height = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);

  const bg = estimateBackground(data, width, height);
  const mask = buildForegroundMask(data, width, height, bg);
  const totalPixels = width * height;

  const toRegion = (box) => {
    if (!box) return null;
    const padX = (box.maxX - box.minX) * PADDING_RATIO;
    const padY = (box.maxY - box.minY) * PADDING_RATIO;
    const inv = 1 / scale;
    return {
      x1: Math.max(0, (box.minX - padX)) * inv,
      y1: Math.max(0, (box.minY - padY)) * inv,
      x2: Math.min(width, (box.maxX + padX)) * inv,
      y2: Math.min(height, (box.maxY + padY)) * inv,
    };
  };

  // Two largest foot-shaped blobs, ordered left-to-right by centroid — matches how the rest of
  // the app assumes "left" = visually left in the (now always vertical) scan.
  const feet = findConnectedComponents(mask, width, height)
    .filter(c => looksLikeFoot(c, totalPixels))
    .sort((a, b) => b.count - a.count)
    .slice(0, 2)
    .sort((a, b) => (a.minX + a.maxX) - (b.minX + b.maxX));

  if (feet.length === 2) return { left: toRegion(feet[0]), right: toRegion(feet[1]) };
  if (feet.length === 1) {
    const isLeft = (feet[0].minX + feet[0].maxX) / 2 < width / 2;
    return { left: isLeft ? toRegion(feet[0]) : null, right: isLeft ? null : toRegion(feet[0]) };
  }
  return { left: null, right: null };
}
